import { FC, useReducer, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Columns, Row } from 'components/layout/profile/profileContent/styles';
import Modal from 'components/modal';
import { MODAL_SIZES } from 'components/modal/types';
import {
  BodyContainer,
  HeaderContainer,
  InnerContainer,
  Title,
} from 'pages/Users/components/InviteUsers/styles';
import {
  formDataReducer,
  FormReducer,
  IAccountSsoProviderError,
  normalizeProviderName,
  COMPANY_NAME_FORMAT_MSG,
  getAttributeNamePrefill,
  getMetadataFieldChanges,
  INPUT_MODE_OPTIONS,
  METADATA_FILE_LOADED_MSG,
  METADATA_FILE_REQUIRED_MSG,
  METADATA_FILE_UNREADABLE_MSG,
  SsoInputMode,
} from './helper';
import SsoProviderForm from './SsoProviderForm';
import SsoProviderInformation from './SsoProviderInformation';
import { SsoProvider } from 'contexts/accounts/types';
import { createSsoProvider, editSsoProvider, CreateSsoProviderPayload } from 'services/accounts';
import Notification from 'components/notification';
import { NOTIFICATION_TYPES } from 'constants/settings';
import {
  certificateWithComments,
  validSlug,
  validateSamlLoginUrl,
} from 'components/utilities/formUtils';
import { getApiErrorMessage } from 'components/utilities/apiErrors';
import {
  MAX_METADATA_XML_LENGTH,
  SAML_METADATA_TOO_LARGE_MSG,
  parseSamlMetadata,
} from 'components/utilities/samlMetadata';
import { Spinner } from 'components/loadings';
import ToggleButtons from 'components/toggleButtons/toggleButtons';

interface IAccountSsoProvider {
  isModalOpen?: boolean;
  ssoProvider: SsoProvider;
  accountId: string;
  onClose: (result?: 'ok' | 'error') => void;
}

const AccountSsoProviderCreate: FC<IAccountSsoProvider> = ({
  isModalOpen,
  ssoProvider,
  accountId,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useReducer(formDataReducer, {
    companyName: ssoProvider?.company_name || '',
    providerName: normalizeProviderName(ssoProvider?.provider_name),
    samlIssuerId: ssoProvider?.saml_issuer_id || '',
    loginUrl: ssoProvider?.login_url || '',
    certificate: ssoProvider?.certificate || '',
    givenName: ssoProvider?.given_name || '',
    familyName: ssoProvider?.family_name || '',
    email: ssoProvider?.email || '',
  });
  const [errors, setErrors] = useState<IAccountSsoProviderError[]>([]);
  const [inputMode, setInputMode] = useState<SsoInputMode>('manual');
  const [metadataXml, setMetadataXml] = useState<string | null>(null);
  const [metadataFileName, setMetadataFileName] = useState('');
  const [metadataError, setMetadataError] = useState('');
  // Only the most recently chosen file may apply its result.
  const latestMetadataRead = useRef(0);
  const {
    companyName,
    providerName,
    samlIssuerId,
    loginUrl,
    certificate,
    givenName,
    familyName,
    email,
  } = formData;

  const handleFormValidations = () => {
    const loginUrlError = loginUrl ? validateSamlLoginUrl(loginUrl) : null;
    if (inputMode === 'upload' && !metadataXml) {
      setMetadataError(METADATA_FILE_REQUIRED_MSG);
      return false;
    } else if (!companyName) {
      setErrors((prevState) => [
        ...prevState,
        { key: 'SET_COMPANY_NAME', msg: 'Company Name required' },
      ]);
      return false;
    } else if (!validSlug(companyName)) {
      setErrors((prevState) => [
        ...prevState,
        { key: 'SET_COMPANY_NAME', msg: COMPANY_NAME_FORMAT_MSG },
      ]);
      return false;
    } else if (!providerName) {
      setErrors((prevState) => [
        ...prevState,
        { key: 'SET_PROVIDER_NAME', msg: 'Provider Name required' },
      ]);
      return false;
    } else if (!samlIssuerId) {
      setErrors((prevState) => [
        ...prevState,
        { key: 'SET_SAML_ISSUER_ID', msg: 'SAML Issuer ID required' },
      ]);
      return false;
    } else if (!loginUrl) {
      setErrors((prevState) => [...prevState, { key: 'SET_LOGIN_URL', msg: 'Login URL required' }]);
      return false;
    } else if (loginUrlError) {
      setErrors((prevState) => [...prevState, { key: 'SET_LOGIN_URL', msg: loginUrlError }]);
      return false;
    } else if (!certificate) {
      setErrors((prevState) => [
        ...prevState,
        { key: 'SET_CERTIFICATE', msg: 'Certificate required' },
      ]);
      return false;
    } else if (certificateWithComments(certificate)) {
      setErrors((prevState) => [
        ...prevState,
        { key: 'SET_CERTIFICATE', msg: 'Please remove comments from your certificate' },
      ]);
      return false;
    } else if (!givenName) {
      setErrors((prevState) => [
        ...prevState,
        { key: 'SET_GIVEN_NAME', msg: 'Given Name required' },
      ]);
      return false;
    } else if (!familyName) {
      setErrors((prevState) => [
        ...prevState,
        { key: 'SET_FAMILY_NAME', msg: 'Family Name required' },
      ]);
      return false;
    } else if (!email) {
      setErrors((prevState) => [...prevState, { key: 'SET_EMAIL', msg: 'Email required' }]);
      return false;
    }
    return true;
  };

  const applyFieldChange = (data: FormReducer) => {
    data.value && setErrors((prev) => prev.filter((error) => error.key !== data.type));
    setFormData({
      type: data.type,
      value: data.value,
    });
  };

  const handleFormChanges = (data: FormReducer) => {
    applyFieldChange(data);
    // Only new configurations get the Entra claim names; saved values are never replaced.
    if (data.type === 'SET_PROVIDER_NAME' && !ssoProvider) {
      getAttributeNamePrefill(data.value, formData).forEach(applyFieldChange);
    }
  };

  const handleInputModeChange = (mode: SsoInputMode) => {
    if (mode === inputMode) return;
    setInputMode(mode);
    setMetadataXml(null);
    setMetadataFileName('');
    setMetadataError('');
    latestMetadataRead.current += 1;
  };

  const handleMetadataFile = (file?: File) => {
    if (!file) return;
    const readId = ++latestMetadataRead.current;
    // Drop everything from the previous file at once: saving stays blocked and none
    // of its name or values show while this one loads or if it turns out invalid.
    setMetadataXml(null);
    setMetadataFileName('');
    setMetadataError('');
    getMetadataFieldChanges().forEach(applyFieldChange);
    // Reject an oversized file before reading it into memory.
    if (file.size > MAX_METADATA_XML_LENGTH) {
      setMetadataFileName(file.name);
      setMetadataXml(null);
      setMetadataError(SAML_METADATA_TOO_LARGE_MSG);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (readId !== latestMetadataRead.current) return;
      const xml = String(reader.result ?? '');
      const result = parseSamlMetadata(xml);
      setMetadataFileName(file.name);
      if ('error' in result) {
        setMetadataXml(null);
        setMetadataError(result.error);
        return;
      }
      // The API validates the file's Login URL too; flag it now, not under a
      // read-only field at Save.
      const loginUrlError = validateSamlLoginUrl(result.values.loginUrl);
      if (loginUrlError) {
        setMetadataError(loginUrlError);
        return;
      }
      setMetadataXml(xml);
      setMetadataError('');
      getMetadataFieldChanges(result.values).forEach(applyFieldChange);
      Notification({ type: NOTIFICATION_TYPES.SUCCESS, text: METADATA_FILE_LOADED_MSG });
    };
    reader.onerror = () => {
      if (readId !== latestMetadataRead.current) return;
      setMetadataXml(null);
      setMetadataError(METADATA_FILE_UNREADABLE_MSG);
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (handleFormValidations()) {
      setLoading(true);
      const ssoProviderData = {
        company_name: formData.companyName,
        provider_name: formData.providerName,
        saml_issuer_id: formData.samlIssuerId,
        login_url: formData.loginUrl,
        certificate: formData.certificate,
        given_name: formData.givenName,
        family_name: formData.familyName,
        email: formData.email,
        // The API re-parses the file and its values take precedence.
        ...(inputMode === 'upload' && metadataXml ? { metadata_xml: metadataXml } : {}),
      } as CreateSsoProviderPayload;

      try {
        ssoProvider
          ? await editSsoProvider(ssoProviderData, accountId, ssoProvider.id)
          : await createSsoProvider(ssoProviderData, accountId);
        const notificationMsg = ssoProvider ? 'SSO provider edited' : 'SSO provider created';
        Notification({ type: NOTIFICATION_TYPES.SUCCESS, text: notificationMsg, duration: 8 });
        setLoading(false);
        onClose('ok');
      } catch (error) {
        const fallbackMsg = ssoProvider
          ? 'Couldn´t edit SSO provider'
          : 'Couldn´t create SSO provider';
        setLoading(false);
        // Backend validation messages can be long; wrap them instead of one wide line.
        Notification({
          type: NOTIFICATION_TYPES.ERROR,
          text: getApiErrorMessage(error, fallbackMsg),
          singleLine: false,
        });
      }
    }
  };

  return (
    <Modal
      centered
      onCancel={() => onClose()}
      visible={isModalOpen}
      fullscreen={isMobile}
      footer={null}
      width={isMobile ? '100%' : MODAL_SIZES.LARGE}
    >
      <InnerContainer>
        <HeaderContainer>
          <Title>Configure SSO/SAML</Title>
          {/* Same placement as the Invite Users modal: the toggle is styled for this header. */}
          <ToggleButtons
            {...INPUT_MODE_OPTIONS}
            onChange={(mode) => handleInputModeChange(mode as SsoInputMode)}
          />
        </HeaderContainer>
        <BodyContainer>
          <Spinner active={loading} top="300px" left="50%" position="relative" />
          <Row>
            <Columns className="setup-information">
              <SsoProviderInformation providerName={providerName} />
            </Columns>
            <Columns className="setup-form">
              <SsoProviderForm
                errors={errors}
                formData={formData}
                loading={loading}
                ssoProvider={ssoProvider}
                inputMode={inputMode}
                metadataFileName={metadataFileName}
                metadataError={metadataError}
                onMetadataFile={handleMetadataFile}
                onCancel={onClose}
                onFieldChange={handleFormChanges}
                onSubmit={handleSubmit}
              />
            </Columns>
          </Row>
        </BodyContainer>
      </InnerContainer>
    </Modal>
  );
};

export default AccountSsoProviderCreate;
